import type { MaskedPattern } from "imask";
import { IMaskMixin, type ReactMaskOpts, type ReactMaskProps } from "react-imask";
import { Input, type InputProps } from "../Input";
import { Textarea, type TextareaProps } from "../Textarea";

export type MaskedInputProps = React.ComponentProps<typeof MaskedInput>;
export type MaskedTextareaProps = React.ComponentProps<typeof MaskedTextarea>;

export const MaskedInput = IMaskMixin<HTMLInputElement, ReactMaskOpts & ReactMaskProps<HTMLInputElement> & InputProps>(
  ({ inputRef, ...props }) => <Input {...props} ref={inputRef} />,
);

export const MaskedTextarea = IMaskMixin<
  HTMLTextAreaElement,
  ReactMaskOpts & ReactMaskProps<HTMLTextAreaElement> & TextareaProps
>(({ inputRef, ...props }) => <Textarea {...props} ref={inputRef} />);

const makeCurrencyMaskProps = (maskProps: Partial<MaskedPattern> = {}) =>
  ({
    mask: [
      { mask: "" },
      {
        mask: "$AMOUNT",
        blocks: {
          AMOUNT: {
            mask: Number,
            radix: ".",
            mapToRadix: [","],
          },
        },
        lazy: false,
        ...maskProps,
      },
    ],
    unmask: true,
  }) satisfies ReactMaskOpts;

const makePercentageMaskProps = (maskProps: Partial<MaskedPattern> = {}) =>
  ({
    mask: [
      { mask: "" },
      {
        mask: "PERCENTAGE%",
        blocks: {
          PERCENTAGE: {
            mask: Number,
            radix: ".",
            mapToRadix: [","],
          },
        },
        lazy: false,
        ...maskProps,
      },
    ],
    unmask: true,
  }) satisfies ReactMaskOpts;

const currencyMaskProps = { ...makeCurrencyMaskProps(), transform: { input: String, output: Number } };
const percentageMaskProps = { ...makePercentageMaskProps(), transform: { input: String, output: Number } };
const wageMaskProps = {
  ...makeCurrencyMaskProps({
    mask: "$WAGE /hr",
    blocks: {
      WAGE: {
        mask: Number,
        scale: 2,
        padFractionalZeros: true,
        normalizeZeros: true,
        radix: ".",
        mapToRadix: [","],
      },
    },
  }),
  transform: { input: String, output: Number },
};

const phoneNumberTransform = {
  input: (value: string | undefined | null) => value?.replace("+1", "") ?? "",
  output: (value: string) => (value ? `+1${value}` : value),
};
const phoneMaskProps = {
  mask: "(000) 000 0000",
  unmask: true,
  transform: phoneNumberTransform,
  placeholder: "(000) 000 0000",
};
const zipCodeMaskProps = { mask: "00000", placeholder: "00000" };

export { currencyMaskProps, percentageMaskProps, phoneMaskProps, wageMaskProps, zipCodeMaskProps };
